import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useAppSelector } from "../../app/hooks";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import {
	ForgotPasswordViewModel,
	useForgotPasswordInfoQuery,
	useForgotPasswordMutation,
} from "./accountApiSlice";

export default function ForgotPassword() {
	const { config } = useAppSelector((state) => state.site);

	const [sent, setSent] = useState<boolean>(false);
	const [error, setError] = useState<string>();
	const [isUsernameRequired, setIsUsernameRequired] = useState<boolean>(false);
	const [email, setEmail] = useState("");
	const [username, setUsername] = useState("");

	const navigate = useNavigate();

	const result = useForgotPasswordInfoQuery();
	const forgotPasswordInfo = result.data;

	const [sendForgotPassword] = useForgotPasswordMutation();

	useEffect(() => {
		if (!forgotPasswordInfo) return;
		setIsUsernameRequired(forgotPasswordInfo.isUsernameRequired);
	}, [forgotPasswordInfo]);

	function handleSend(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>): void {
		e.preventDefault();
		const model = UpdateModel();

		if (model) {
			sendForgotPassword(model)
				.unwrap()
				.then(() => {
					setSent(true);
				})
				.catch((error) => {
					console.log(error);
					setIsUsernameRequired(error.data.model.isUsernameRequired);
					setError(error.data.errorMsg);
					scrollTo(0, 0);
				});
		}
	}

	function UpdateModel(): ForgotPasswordViewModel | null {
		if (!forgotPasswordInfo) return null;
		const model: ForgotPasswordViewModel = {
			...forgotPasswordInfo,
			email: email,
			username: username,
			isUsernameRequired: isUsernameRequired,
		};
		return model;
	}

	return (
		<section>
			<div>
				<div className="pt-8">
					<h1 className="">Forgot Password</h1>
					{!sent ? (
						<div>
							<p className="mt-4">
								If you are a student, please contact your teacher for help resetting
								your password.
							</p>
							<p className="mt-4">
								If you are an administrator or teacher, please enter your account
								email below to reset your password.
							</p>
							<AlertMessage
								className="mt-8"
								type={AlertLevel.Warning}
								message={error}
								showIcon={true}
								onClear={() => setError(undefined)}
							/>
						</div>
					) : (
						<div className="mt-4">
							<h2>Check your email</h2>
							<p className="mt-4">
								{`We sent you an email to ${email} from ${config?.supportFromEmail}. The message has a link you can click to reset your password.`}{" "}
							</p>
							<p className="mt-4 max-w-5xl">
								If you do not receive the email in either your inbox or spam folder,
								please check with your IT department to make sure emails from this
								email address do not end up in your spam filter or are blocked by
								your IT department.
							</p>
							<p className="mt-4">
								<a
									className="hover:underline hover:underline-offset-2 hover:cursor-pointer"
									onClick={() => navigate("/login")}>
									Click here
								</a>{" "}
								to return to the login page.
							</p>
						</div>
					)}
				</div>

				{!sent && (
					<form>
						<div>
							<label
								htmlFor="email"
								className="block mt-6 font-medium leading-6 text-gray-900">
								Email Address
							</label>
							<div className="mt-2">
								<input
									id="email"
									name="email"
									type="email"
									required
									maxLength={forgotPasswordInfo?.rules.emailMaxLength}
									onChange={(e) => setEmail(e.target.value)}
									className="block w-1/2 max-w-96 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
								/>
							</div>
						</div>
						{isUsernameRequired && (
							<div>
								<label
									htmlFor="username"
									className="block mt-6 font-medium leading-6 text-gray-900">
									Username
								</label>
								<div className="mt-2">
									<input
										type="text"
										name="username"
										id="username"
										maxLength={forgotPasswordInfo?.rules.userNameMaxLength}
										onChange={(e) => setUsername(e.target.value)}
										className="block w-1/2 max-w-96 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
									/>
								</div>
							</div>
						)}
						<button
							type="submit"
							className="btn btn-primary mt-6"
							onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSend(e)}>
							Send
						</button>
					</form>
				)}

				<div className="py-8">
					<h3>Still having trouble? </h3>
					<p>
						<a
							className="hover:underline hover:underline-offset-2 hover:cursor-pointer"
							href={config?.contactUsUrl}>
							Contact us
						</a>{" "}
						for further assistance.
					</p>
				</div>
			</div>
		</section>
	);
}
